import axios from 'axios';
import { clients } from '../clients';
import { getWorkstationNumber } from '../workstation-util';

let baseUrl;
let privateKey;
let creditFeePercentage;
let debitFeePercentage;
let debitAmountLimit;
let debitAmountFlat;
let eCheckFee;

const { backendRest } = clients.direct;

function getCardType(binNumber) {
  if (/^4[0-9]{5}/.test(binNumber)) return 'VISA';
  if (/^5[1-5][0-9]{4}/.test(binNumber)) return 'MC';
  if (/^3[47][0-9]{4}/.test(binNumber)) return 'AMEX';
  if (/^6(?:011|5[0-9]{2})[0-9]{3}/.test(binNumber)) return 'DISCOVER';
  return 'OTHER';
}
const getCardBinType = async (token) => {
  try {
    if (!token || typeof token !== 'string') {
      return { error: 'Invalid token provided' };
    }
    const url = `${baseUrl}/tokens/?expand[payment][bin][]=`;
    const tokenQuery = `token[equals]=${token}`;
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        APIKEY: privateKey,
        search: tokenQuery,
      },
    });
    const resData = response && response.data
      && response.data.response && response.data.response.data
      ? response.data.response.data
      : null;

    if (!resData || resData.length === 0) throw new Error('No data returned from API');
    const tokenData = resData[0];

    const paymentData = tokenData.payment;
    const binNumber = tokenData.payment && tokenData.payment.bin && tokenData.payment.bin.bin;
    const cardType = getCardType(binNumber);
    return { cardType, paymentData };
  } catch (error) {
    return { error: error.message };
  }
};
const calculateFee = (cardBinType, binNumber, amountInCents) => {
  let fee = 0;
  if (cardBinType === 'credit') {
    if (binNumber === '424631') {
      creditFeePercentage = 3.25; // Special case for this bin to have 3.25% fee
    }
    fee = Math.round((amountInCents * creditFeePercentage) / 100);
  } else if (cardBinType === 'debit') {
    if (debitAmountLimit > 0 && (amountInCents / 100) <= debitAmountLimit) {
      fee = debitAmountFlat * 100;
    } else {
      fee = Math.round((amountInCents * debitFeePercentage) / 100);
    }
  }
  if (fee === 0 && cardBinType && cardBinType !== 'credit' && cardBinType !== 'debit') {
    console.warn(`Unknown card bin type: ${cardBinType}, applying no fee`);
  }
  return fee;
};

const tokenizedResponse = async (requestBody) => {
  const payload = {
    requestBody,
    baseUrl,
    privateKey,
  };

  try {
    const payrixResponse = await backendRest.post('/tax/ccPayments/payrix/process', payload, {
    });

    const responseAfterProcessing = payrixResponse.data;
    return responseAfterProcessing;
  } catch (error) {
    console.error('Error processing tokenized payment: ', error);
    if (error.response) {
      const statusCode = error.response.status;
      const responseBody = error.response.data;
      if (statusCode >= 400) {
        console.error('Error response body:', responseBody);
        const enhancedError = new Error(
          responseBody.error || 'Payment processing failed',
        );
        enhancedError.statusCode = statusCode;
        enhancedError.responseBody = responseBody;
        throw enhancedError;
      }
    }
    throw error;
  }
};

const calculateTotalWithFee = (amountInCents, fee) => Number(amountInCents) + Number(fee);

const pollTransactionStatus = async (transactionId) => {
  const payload = {
    txnId: transactionId,
    baseUrl,
    privateKey,
    // Payrix can take longer than the server's default 3x1s poll budget to move a txn
    // out of pending; a larger budget keeps slow-settling approvals from failing here.
    retries: 10,
    interval: 1500,
  };
  try {
    const getTxnResults = await backendRest.post('/tax/ccPayments/payrix/status', payload, {
    });
    const ultimateStatus = getTxnResults.data;
    return ultimateStatus;
  } catch (error) {
    console.error('Payment processing error: ', error);
    throw error;
  }
};

const stringifyCartContents = (cartContents, paymentContext = 'TAX', maxLength = 1000) => {
  const stripCommas = (val) => (val ? String(val).replace(/,/g, '') : val);
  const roundToHundredth = (val) => (val != null ? Math.round(Number(val) * 100) / 100 : val);
  if (paymentContext === 'TAX') {
    const stringifiedData = (cartContents || []).map((item) => `${item.taxYear},TID:${item.taxId},Amt:${stripCommas(item.totalTax)},Pen:${stripCommas(item.penaltyAmt)},Fee:${stripCommas(item.totalFees)},Tot:${roundToHundredth(stripCommas(item.total))}`);
    let result = '';
    for (let i = 0; i < stringifiedData.length; i += 1) {
      const separator = i === 0 ? '' : '|';
      const nextSegment = separator + stringifiedData[i];
      if (result.length + nextSegment.length >= maxLength) {
        break;
      }
      result += nextSegment;
    }
    return result;
  }
  if (paymentContext === 'OD') {
    if (!cartContents) return '';
    return `FY:${cartContents.fiscalYear},Date:${cartContents.register},RegNo:${cartContents.registration},Dept:${cartContents.department},Type:${cartContents.type},Amt:${stripCommas(cartContents.amount)}`;
  }
  if (paymentContext === 'MISC') {
    if (!cartContents) return '';
    const parts = [];
    if (cartContents.receiptNumber) parts.push(`RcptNo:${stripCommas(cartContents.receiptNumber)}`);
    if (cartContents.receivedOf) parts.push(`RcvdOf:${stripCommas(cartContents.receivedOf)}`);
    return parts.join(',');
  }
  if (paymentContext === 'MTG') {
    if (!cartContents) return '';
    const parts = [];
    if (cartContents.receiptNumber) parts.push(`RcptNo:${stripCommas(cartContents.receiptNumber)}`);
    if (cartContents.receivedOf) parts.push(`RcvdOf:${stripCommas(cartContents.receivedOf)}`);
    return parts.join(',');
  }
  return '';
};

/**
 * Performs bin check and fee calculation without submitting payment
 * @returns {Object} { cardType, binType, fee, totalWithFee } or { error }
 */
export const getBinCheckInfo = async (token, amountDue, paymentConfig) => {
  baseUrl = paymentConfig.payrixBaseUrl;
  privateKey = paymentConfig.payrixPrivateKey;
  creditFeePercentage = paymentConfig.payrixCreditPercentage;
  debitFeePercentage = paymentConfig.payrixDebitPercentage;
  debitAmountLimit = paymentConfig.payrixDebitAmtLimit;
  debitAmountFlat = paymentConfig.payrixDebitFeeFlat;

  const dueAmountInCents = amountDue * 100;

  try {
    const { cardType, paymentData, error } = await getCardBinType(token);
    if (error) {
      return { error };
    }

    const binType = paymentData.bin.type;
    if (!binType) {
      return { error: 'Unable to determine card bin type from API response' };
    }

    const fee = calculateFee(binType, paymentData.bin.bin, dueAmountInCents);
    const totalWithFee = Math.round(dueAmountInCents + fee);

    return {
      cardType,
      binType,
      fee, // in cents
      totalWithFee, // in cents
      paymentData,
    };
  } catch (err) {
    console.error('Error during bin check:', err);
    return { error: err.message };
  }
};

export const processPayment = async (
  token, phone, email, amountDue, cartContents, paymentConfig, payrixPaymentType, paymentContext = 'TAX', unformattedOwnerName, requestId, preCalculatedBinInfo = null, merchantId,
) => {
  let totalWithFee;
  let cardType;
  let paymentData;
  let calculatedFee;
  let reason = '';
  baseUrl = paymentConfig.payrixBaseUrl;
  privateKey = paymentConfig.payrixPrivateKey;
  creditFeePercentage = paymentConfig.payrixCreditPercentage;
  debitFeePercentage = paymentConfig.payrixDebitPercentage;
  debitAmountLimit = paymentConfig.payrixDebitAmtLimit;
  debitAmountFlat = paymentConfig.payrixDebitFeeFlat;
  eCheckFee = paymentConfig.payrixEcheckFee;
  const wkst = getWorkstationNumber() || '99';
  const sanitizedPhoneNumber = phone ? phone.replace(/[\s-()/]/g, '') : null;
  const emailInput = email || null;
  const dueAmountInCents = amountDue * 100;
  const ownerNameUnformatted = unformattedOwnerName || '';
  const splitOwnerName = ownerNameUnformatted.split(',');
  const formattedFirstOwnerName = splitOwnerName[1] || '';
  const formattedLastOwnerName = splitOwnerName[0] || '';
  const formattedFullOwnerName = `${formattedFirstOwnerName} ${formattedLastOwnerName}`.trim();

  if (payrixPaymentType === 'CREDIT') { // note: the usage of 'CREDIT' in this line has no relevance to card bin type. It is used to distinguish between 'CARD' and 'ECHECK'
    // Use pre-calculated bin info if provided (from verify step), otherwise fetch it
    if (preCalculatedBinInfo) {
      cardType = preCalculatedBinInfo.cardType;
      paymentData = preCalculatedBinInfo.paymentData;
      calculatedFee = preCalculatedBinInfo.fee;
      totalWithFee = preCalculatedBinInfo.totalWithFee;
    } else {
      // Fallback: fetch bin info if not pre-calculated (legacy flow)
      ({ cardType, paymentData } = await getCardBinType(token));

      const cardBinType = paymentData.bin.type;
      if (!cardBinType) {
        throw new Error('Unable to determine card bin type from API response');
      }
      calculatedFee = calculateFee(cardBinType, paymentData.bin.bin, dueAmountInCents);
      totalWithFee = calculateTotalWithFee(dueAmountInCents, calculatedFee);
      totalWithFee = Math.round(totalWithFee);

      // Only show confirm dialog in legacy flow (when bin info wasn't pre-verified)
      const userConfirmed = window.confirm(
        `CARD TYPE: ${cardType.toUpperCase()} ${cardBinType.toUpperCase()} \n`
      + `FEE: $${(calculatedFee / 100).toFixed(2)} \n`
      + `PAYMENT TOTAL (including fee): $${(totalWithFee / 100).toFixed(2)} \n\n`
      + 'Proceed with payment?',
      );

      if (!userConfirmed) {
        console.info('Payrix confirmation dialog: user clicked Cancel', {
          totalWithFee,
        });
        reason = 'canceled';
        return { success: false, transactionId: null, reason };
      }
    }
  }

  if (payrixPaymentType === 'ECHECK') {
    calculatedFee = eCheckFee * 100;
    totalWithFee = calculateTotalWithFee(dueAmountInCents, calculatedFee);
    totalWithFee = Math.round(totalWithFee);
  }

  try {
    const stringifiedCartContents = stringifyCartContents(cartContents, paymentContext);
    const requestBody = {
      merchant: merchantId || paymentConfig.posMerchantId,
      total: totalWithFee, // in cents
      // Payrix rejects fee=0 ("only accepts integers between 1 and 9000000000"),
      // so omit the field entirely when there is no surcharge (e.g. waived fees).
      ...(calculatedFee > 0 && { fee: calculatedFee }), // in cents
      token,
      origin: 2,
      type: payrixPaymentType === 'CREDIT' ? 1 : 7,
      terminal: wkst,
      order: stringifiedCartContents,
      phone: sanitizedPhoneNumber,
      email: emailInput,
      // important! we are using PayFields "company" field
      // to send owner name in case cardholder name is empty
      company: formattedFullOwnerName,
      paymentData,
      requestId,
    };
    const tokenizedResultData = await tokenizedResponse(requestBody);
    if (!tokenizedResultData) throw new Error('Empty response from Payrix');
    const { id } = tokenizedResultData;
    const { authorization } = tokenizedResultData;
    const phoneNumb = tokenizedResultData.phone;
    const emailAdd = tokenizedResultData.email;
    const firstName = tokenizedResultData.first || '';
    const lastName = tokenizedResultData.last || '';
    const ownerName = tokenizedResultData.company || '';
    const cardholderName = (firstName.length > 0 && lastName.length > 0)
      ? `${firstName} ${lastName}`.trim()
      : ownerName; // try to use cc holder name, otherwise use owner name from tax record
    const processingFee = tokenizedResultData.fee;
    const paidAmtWithProcFee = tokenizedResultData.total;

    if (id === 'error') throw new Error('Transaction ID error. Please try again.');
    if (authorization === 'error') throw new Error('Card was unable to be authorized.');

    const paymentStatus = await pollTransactionStatus(id);
    if (!paymentStatus) {
      // Charge may already be approved at Payrix even though polling never saw it leave
      // pending — log the txn id so support can reconcile from a console/HAR capture.
      console.warn(`Payrix txn ${id} still pending after status polling; payment records were not saved automatically. Check ReNewGov portal for the final status.`);
      alert('Please check your ReNewGov Portal for the status of this payment');
      return { success: false, transactionId: null, reason: 'unknown_status' };
    }

    // Handle different payment statuses
    switch (paymentStatus) {
      case 1: // approved
      case 3: // captured
      case 4: // settled
        // Success - continue to return statement below
        break;
      case 2: // declined
        return {
          success: false,
          transactionId: id,
          reason: 'declined',
          errorMessage: 'Payment was Declined',
        };
      default: // Unexpected status
        console.warn(`Payrix txn ${id} returned unexpected status ${paymentStatus}; payment records were not saved automatically.`);
        return {
          success: false,
          transactionId: id,
          reason: 'unknown_status',
        };
    }

    return {
      success: true,
      transactionId: id,
      phoneNumber: phoneNumb,
      emailAddress: emailAdd,
      cardholderName,
      cardType,
      processingFee,
      paidAmtWithProcFee,
      reason,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Payment processing failed',
      transactionId: null,
    };
  }
};

export default processPayment;
